import React from 'react';
import {
  TARIFFS_LEGACY_SUNSET_DISPLAY,
  TARIFFS_VERSION_KEY,
  isTariffsLegacyRetired,
  type TariffsVersion,
} from './version.constants';
import {
  getPersistedTariffsVersion,
  recordTariffsVersionChange,
  setTariffsVersionProperty,
  type TariffsVersionSource,
} from './versionTracking';

/**
 * Default to NEW; only an explicit, persisted 'LEGACY' opt-out serves the old module — and once the sunset has passed
 * the new one is mandatory, so storage is ignored. Anyone who never chose (which is everyone, the first time) lands on
 * the rebuilt module.
 */
const readInitialVersion = (): TariffsVersion => {
  if (isTariffsLegacyRetired()) return 'NEW';
  try {
    return localStorage.getItem(TARIFFS_VERSION_KEY) === 'LEGACY' ? 'LEGACY' : 'NEW';
  } catch {
    return 'NEW';
  }
};

const persistVersion = (value: TariffsVersion): void => {
  try {
    localStorage.setItem(TARIFFS_VERSION_KEY, value);
  } catch {
    // ignore storage failures (private mode etc.) — in-memory state still applies for the session
  }
};

interface TariffsVersionContextValue {
  version: TariffsVersion;
  isLegacy: boolean;
  /** Once the sunset has passed the new module is mandatory: no reverting, and no affordance offering it. */
  legacyRetired: boolean;
  /** The sunset date shown to users (pt-BR), e.g. '30/09/2026'. */
  sunsetDisplay: string;
  setVersion: (value: TariffsVersion, source?: TariffsVersionSource) => void;
}

const VersionContext = React.createContext<TariffsVersionContextValue | null>(null);

/**
 * Shares the chosen tariffs version between the module's own chrome and the route that picks a page.
 *
 * A context and not a plain read of localStorage because the switch has to be instant: the settings menu lives INSIDE
 * the new module, so the click that reverts is dispatched by a component that is about to be unmounted, and the page
 * above it has to re-render from the same state rather than wait for a reload.
 */
export const TariffsVersionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [version, setVersionState] = React.useState<TariffsVersion>(readInitialVersion);

  // Evaluated once per mount (like the nav layout and the cash-position version); a session that crosses the sunset
  // picks up the mandatory version on its next reload.
  const legacyRetired = React.useMemo(() => isTariffsLegacyRetired(), []);

  const setVersion = React.useCallback(
    (value: TariffsVersion, source: TariffsVersionSource = 'settings_menu') => {
      // After the sunset the new module is mandatory — ignore any attempt to go back.
      const target: TariffsVersion = legacyRetired ? 'NEW' : value;
      // recordTariffsVersionChange reads the previous value from storage, so record BEFORE persisting.
      recordTariffsVersionChange(target, source);
      setVersionState(target);
      persistVersion(target);
    },
    [legacyRetired],
  );

  // Once the sunset has passed, force-migrate whoever is still on LEGACY exactly once — recorded as 'deadline_forced'
  // so forced and voluntary switches stay distinguishable — then reconcile storage.
  React.useEffect(() => {
    if (!legacyRetired) return;
    if (getPersistedTariffsVersion() !== 'NEW') {
      recordTariffsVersionChange('NEW', 'deadline_forced');
    }
    persistVersion('NEW');
  }, [legacyRetired]);

  React.useEffect(() => {
    setTariffsVersionProperty(version);
  }, [version]);

  const value = React.useMemo(
    () => ({
      version,
      isLegacy: version === 'LEGACY',
      legacyRetired,
      sunsetDisplay: TARIFFS_LEGACY_SUNSET_DISPLAY,
      setVersion,
    }),
    [version, legacyRetired, setVersion],
  );

  return <VersionContext.Provider value={value}>{children}</VersionContext.Provider>;
};

/**
 * The active version. Returns `null` outside a provider on purpose: the module's chrome is also rendered by the
 * standalone preview harness and by the forced-new route, where there is no migration to offer — so the caller hides
 * the affordance instead of the app crashing.
 */
export const useTariffsVersion = (): TariffsVersionContextValue | null => React.useContext(VersionContext);
